import { useState } from 'react';
import {
  Flex,
  Text,
  Grid,
  Button,
  Tooltip,
  TextArea,
  Select,
} from '@radix-ui/themes';
import { DiscordLogoIcon } from '@radix-ui/react-icons';
import { useEdges } from 'reactflow';
import { useCreateDiscordMessageMutation } from '../../../../../graphql/queries/CreateDiscordMessage.generated';
import { useUpdateDiscordMessageMutation } from '../../../../../graphql/queries/UpdateDiscordMessage.generated';

import TemplateContentRenderer from '../../../../TemplateContentRenderer';
import {
  ActionNode,
  DiscordMessageAction,
  DiscordMessageType,
} from '../../../../../types';
import WebhookSelect from '../../../../WebhookSelect';
import DiscordBotSelect from '../../../../DiscordBotSelect';
import DiscordBotChannelSelect from '../../../../DiscordBotChannelSelect';
import { NodeInput } from '../../../inputs';
import { WorkflowNodeProps } from '../..';
import NodeContainer from '../../Container';
import CategorySection from '../../CategorySection';
import { toast } from '../../../../../utils/toast';

const DiscordMessageNode = ({
  id,
  data,
  onChange: handleSave,
}: WorkflowNodeProps<ActionNode>) => {
  const discordAction = data.action as DiscordMessageAction;
  const discordMessage = discordAction?.discordMessage;
  const webhook = discordMessage?.webhook;
  const bot = discordMessage?.bot;
  const botChannel = discordMessage?.botChannel;
  const isExistingMessage = !!discordMessage?.id;

  const [isEditing, setIsEditing] = useState(!webhook?.id && !bot?.id);
  const [messageType, setMessageType] = useState<DiscordMessageType>(
    discordMessage?.messageType || DiscordMessageType.Webhook,
  );
  const [selectedWebhookId, setSelectedWebhookId] = useState<
    string | undefined
  >(webhook?.id);
  const [selectedBotId, setSelectedBotId] = useState<string | undefined>(
    bot?.id,
  );
  const [selectedChannelId, setSelectedChannelId] = useState<
    string | undefined
  >(botChannel?.id);
  const [messageContent, setMessageContent] = useState(discordMessage?.content);
  const edges = useEdges();

  const [createDiscordMessage, { loading: isCreating }] =
    useCreateDiscordMessageMutation();
  const [updateDiscordMessage, { loading: isUpdating }] =
    useUpdateDiscordMessageMutation();

  const hasMessageEdge = edges.some(
    (edge) => edge.target === id && edge.targetHandle === 'message',
  );

  const onSave = () => {
    // Validation based on message type
    if (messageType === DiscordMessageType.Webhook && !selectedWebhookId)
      return;
    if (
      messageType === DiscordMessageType.Bot &&
      (!selectedBotId || !selectedChannelId)
    )
      return;
    if (!messageContent) return;

    toast.info('Saving Discord message...');

    const isCreate = !discordAction?.discordMessage?.id;

    const mutation = isCreate
      ? createDiscordMessage({
          variables: {
            input: {
              messageType,
              webhookId:
                messageType === DiscordMessageType.Webhook
                  ? selectedWebhookId
                  : undefined,
              botId:
                messageType === DiscordMessageType.Bot
                  ? selectedBotId
                  : undefined,
              channelId:
                messageType === DiscordMessageType.Bot
                  ? selectedChannelId
                  : undefined,
              content: messageContent,
            },
          },
        })
      : updateDiscordMessage({
          variables: {
            input: {
              id: discordAction.discordMessage.id,
              content: messageContent,
            },
          },
        });

    mutation
      .then((result) => {
        // Call the parent's onChange to update local state
        if (handleSave) {
          const savedMessage = isCreate
            ? result.data?.createDiscordMessage
            : result.data?.updateDiscordMessage;

          handleSave(id, {
            ...data,
            action: {
              ...data.action,
              discordMessage: savedMessage,
            },
          });
        }

        toast.success('Discord message saved successfully');
        setIsEditing(false);
      })
      .catch((error) => {
        console.error('Failed to save Discord message:', error);
        toast.error(
          `Failed to ${isCreate ? 'create' : 'update'} Discord message`,
        );
      });
  };

  const handleEditToggle = () => {
    setIsEditing(true);
  };

  return (
    <NodeContainer
      title="Discord Message"
      data={data}
      icon={<DiscordLogoIcon width={16} height={16} />}
      onEdit={handleEditToggle}
      isEditing={isEditing}
    >
      <CategorySection title="Settings">
        <Grid columns="1" gap="3">
          <NodeInput title="Message Type">
            {isExistingMessage ? (
              <Tooltip content="Message type cannot be changed after creation">
                <span style={{ display: 'block', width: '100%' }}>
                  <Select.Root
                    value={messageType}
                    onValueChange={(value) =>
                      setMessageType(value as DiscordMessageType)
                    }
                    disabled={true}
                  >
                    <Select.Trigger style={{ width: '100%' }} />
                    <Select.Content>
                      <Select.Item value={DiscordMessageType.Webhook}>
                        Webhook
                      </Select.Item>
                      <Select.Item value={DiscordMessageType.Bot}>
                        Bot
                      </Select.Item>
                    </Select.Content>
                  </Select.Root>
                </span>
              </Tooltip>
            ) : (
              <Select.Root
                value={messageType}
                onValueChange={(value) =>
                  setMessageType(value as DiscordMessageType)
                }
                disabled={!isEditing}
              >
                <Select.Trigger style={{ width: '100%' }} />
                <Select.Content>
                  <Select.Item value={DiscordMessageType.Webhook}>
                    Webhook
                  </Select.Item>
                  <Select.Item value={DiscordMessageType.Bot}>Bot</Select.Item>
                </Select.Content>
              </Select.Root>
            )}
          </NodeInput>

          {messageType === DiscordMessageType.Webhook && (
            <NodeInput title="Webhook">
              {isExistingMessage ? (
                <Tooltip content="Webhook cannot be changed after creation">
                  <span style={{ display: 'block', width: '100%' }}>
                    <WebhookSelect
                      webhookId={selectedWebhookId}
                      onValueChange={setSelectedWebhookId}
                      disabled={true}
                    />
                  </span>
                </Tooltip>
              ) : (
                <WebhookSelect
                  webhookId={selectedWebhookId}
                  onValueChange={setSelectedWebhookId}
                  disabled={!isEditing}
                />
              )}
            </NodeInput>
          )}

          {messageType === DiscordMessageType.Bot && (
            <>
              <NodeInput title="Bot">
                {isExistingMessage ? (
                  <Tooltip content="Bot cannot be changed after creation">
                    <span style={{ display: 'block', width: '100%' }}>
                      <DiscordBotSelect
                        botId={selectedBotId}
                        onValueChange={setSelectedBotId}
                        disabled={true}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <DiscordBotSelect
                    botId={selectedBotId}
                    onValueChange={setSelectedBotId}
                    disabled={!isEditing}
                  />
                )}
              </NodeInput>
              <NodeInput title="Channel">
                {isExistingMessage ? (
                  <Tooltip content="Channel cannot be changed after creation">
                    <span style={{ display: 'block', width: '100%' }}>
                      <DiscordBotChannelSelect
                        botId={selectedBotId}
                        channelId={selectedChannelId}
                        onValueChange={setSelectedChannelId}
                        disabled={true}
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <DiscordBotChannelSelect
                    botId={selectedBotId}
                    channelId={selectedChannelId}
                    onValueChange={setSelectedChannelId}
                    disabled={!isEditing}
                  />
                )}
              </NodeInput>
            </>
          )}

          <NodeInput title="Message">
            {!hasMessageEdge && (
              <>
                {isEditing ? (
                  <TextArea
                    size="1"
                    value={messageContent || ''}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      setMessageContent(e.target.value)
                    }
                    placeholder="Enter your Discord message..."
                  />
                ) : (
                  <TemplateContentRenderer
                    content={messageContent || 'No message content'}
                  />
                )}
              </>
            )}
            {hasMessageEdge && (
              <Text size="1" color="gray">
                (Connected via edge)
              </Text>
            )}
          </NodeInput>

          {isEditing && (
            <Flex justify="end" gap="2">
              {(() => {
                const isWebhookValid =
                  messageType === DiscordMessageType.Webhook &&
                  selectedWebhookId;
                const isBotValid =
                  messageType === DiscordMessageType.Bot &&
                  selectedBotId &&
                  selectedChannelId;
                const isMessageValid = hasMessageEdge || messageContent;
                const isValid =
                  (isWebhookValid || isBotValid) && isMessageValid;

                if (!isValid) {
                  let tooltipContent = '';
                  if (messageType === DiscordMessageType.Webhook) {
                    tooltipContent =
                      'Webhook and Message content (or edge) required';
                  } else {
                    tooltipContent =
                      'Bot, Channel, and Message content (or edge) required';
                  }

                  return (
                    <Tooltip content={tooltipContent}>
                      <Button size="1" disabled={true}>
                        Save
                      </Button>
                    </Tooltip>
                  );
                }

                return (
                  <Button
                    size="1"
                    onClick={onSave}
                    disabled={isCreating || isUpdating}
                  >
                    {isCreating || isUpdating ? 'Saving...' : 'Save'}
                  </Button>
                );
              })()}
            </Flex>
          )}
        </Grid>
      </CategorySection>
    </NodeContainer>
  );
};

export default DiscordMessageNode;
